import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Toast from '../components/Toast';
import {
  getCommentBank,
  updateFeedbackBanks,
  createNewSmartAnnotation,
  updateSmartAnnotation,
  deleteSmartAnnotation
} from '../service';
import { fetchSubjectsByState } from '../utils/subjects';
import { toolSettingsPatch } from '../utils/toolSettingsPatch';
import { useUserStates } from './useUserStates';

const useCommentBank = (commentBankId) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { states: userStates } = useUserStates();
  const userState = userStates;
  const [commentBank, setCommentBank] = useState(null);
  const [subjectsList, setSubjectsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentBank = useRef(null);
  const saveTail = useRef(Promise.resolve());
  const activeId = useRef({ id: commentBankId });
  if (activeId.current.id !== commentBankId) activeId.current = { id: commentBankId };
  const owner = activeId.current;

  // Fetch subjects for metadata building
  useEffect(() => {
    const loadSubjects = async () => {
      if (userState.length > 0) {
        const subjects = await fetchSubjectsByState(userState);
        setSubjectsList(subjects);
      }
    };
    loadSubjects();
  }, [JSON.stringify(userState)]);

  useEffect(() => {
    let cancelled = false;
    if (!commentBankId) {
      setIsLoading(false);
      return;
    }
    const fetchCommentBank = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getCommentBank(commentBankId);
        if (cancelled) return;

        if (!data) {
          throw new Error('Comment bank not found');
        }

        if (!data.smartComments) {
          data.smartComments = [];
        } else {
          data.smartComments = data.smartComments.filter(comment => comment != null);
        }

        const metadata = data.metadata || {};
        const years = Array.isArray(metadata.years) ? metadata.years : [];
        const subjects = Array.isArray(metadata.subjects) ? metadata.subjects : [];
        const taskTypes = Array.isArray(metadata.taskTypes) ? metadata.taskTypes : [];
        const responseTypes = Array.isArray(metadata.responseTypes) ? metadata.responseTypes : [];

        const loaded = {
          ...data,
          years,
          subjects,
          taskTypes,
          responseTypes,
          metadata,
        };
        currentBank.current = loaded;
        setCommentBank(loaded);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching comment bank:', err);
        setError(err);
        currentBank.current = null;
        setCommentBank(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCommentBank();
    return () => { cancelled = true; };
  }, [commentBankId]);

  useEffect(() => {
    if (error) {
      toast(<Toast message="Comment bank not found" />);
      history.push('/tools/commentbanks');
    }
  }, [error, history]);

  const updateCommentBank = async (updatedData) => {
    const pending = saveTail.current.catch(() => {}).then(async () => {
      const current = currentBank.current;
      if (!current || activeId.current !== owner || (current.id || current._id) !== commentBankId) return;
      try {
        const patch = toolSettingsPatch(updatedData, current, subjectsList);
        await updateFeedbackBanks(patch, commentBankId);
        const latest = currentBank.current;
        if (activeId.current !== owner || (latest?.id || latest?._id) !== commentBankId) return;
        const updated = { ...latest, ...updatedData, ...(patch.metadata ? { metadata: patch.metadata } : {}) };
        currentBank.current = updated;
        setCommentBank(updated);
        queryClient.invalidateQueries({ queryKey: ['commentBanks'] });
        queryClient.invalidateQueries({ queryKey: ['commentBanksWithPreferences'] });
        toast(<Toast message="Comment bank updated successfully" />);
      } catch (err) {
        toast(<Toast message="Error updating comment bank" />);
        throw err;
      }
    });
    saveTail.current = pending;
    return pending;
  };

  const applySmartCommentsUpdate = async (newSmartComments, successMessage, errorMessage) => {
    const previousCommentBank = currentBank.current;
    if (!previousCommentBank || activeId.current !== owner) return;
    const finalSmartComments = newSmartComments.filter(c => c != null);

    // Optimistic: update UI immediately
    currentBank.current = { ...previousCommentBank, smartComments: finalSmartComments };
    setCommentBank(currentBank.current);

    try {
      const pending = saveTail.current.catch(() => {}).then(() => {
        if (activeId.current !== owner) return;
        return updateSmartAnnotation({ smartComments: finalSmartComments }, commentBankId);
      });
      saveTail.current = pending;
      await pending;
      if (activeId.current === owner && successMessage) toast(<Toast message={successMessage} />);
    } catch (err) {
      // Roll back only this content edit, preserving later settings and drafts.
      if (activeId.current === owner && currentBank.current?.smartComments === finalSmartComments) {
        currentBank.current = { ...currentBank.current, smartComments: previousCommentBank.smartComments };
        setCommentBank(currentBank.current);
      }
      toast(<Toast message={errorMessage} />);
      throw err;
    }
  };

  const createSmartAnnotation = async (newSmartAnnotation) => {
    const cleanSmartComments = (currentBank.current?.smartComments || []).filter(c => c != null);
    await applySmartCommentsUpdate(
      [...cleanSmartComments, newSmartAnnotation],
      'Smart annotation created',
      'Error creating smart annotation'
    );
  };

  const updateSmartAnnotationHandler = async (updatedSmartComment, index) => {
    const cleanSmartComments = (currentBank.current?.smartComments || []).filter(c => c != null);
    const updated = cleanSmartComments.map((c, i) => (i === index ? updatedSmartComment : c));
    await applySmartCommentsUpdate(updated, 'Smart annotation updated', 'Error updating smart annotation');
  };

  const deleteAnnotationHandler = async (index) => {
    const cleanSmartComments = (currentBank.current?.smartComments || []).filter(c => c != null);
    const updated = cleanSmartComments.filter((_, i) => i !== index);
    await applySmartCommentsUpdate(updated, 'Smart comment deleted', 'Error deleting smart comment');
  };

  const createSmartAnnotationHandler = () => {
    const smartAnnotationRequest = {
      title: 'Untitled feedback area',
      suggestions: ['First comment', 'Second comment']
    };
    createSmartAnnotation(smartAnnotationRequest);
  };

  const reorderSmartCommentsHandler = async (reorderedItems) => {
    return applySmartCommentsUpdate(reorderedItems, null, 'Error reordering comments');
  };

  return {
    commentBank,
    isLoading,
    error,
    updateCommentBank,
    createSmartAnnotation,
    updateSmartAnnotationHandler,
    deleteAnnotationHandler,
    createSmartAnnotationHandler,
    reorderSmartCommentsHandler
  };
};

export default useCommentBank;
